import { ChevronLeft } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { convertRegistrationData, registerCustomer } from '../api/auth';
// import { useAuth } from '../hooks';
import { useAuth } from '@/auth/AuthContext';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { routes } from '../config';
import { useStoreSettings } from '../contexts/StoreSettingsContext';
import type { FirebaseUser, RegisterData } from '../types';
import { LanguageSelector } from '@/components/LanguageSelector';
import { useLocalization } from '@/contexts/LocalizationContext';

interface RegisterProps {
  firebaseUser?: FirebaseUser | null;
  onBack: () => void;
  initialFormData?: RegisterData | null;
}

export default function Register({ firebaseUser, onBack, initialFormData }: RegisterProps) {
  const { t } = useLocalization();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { updateCustomer } = useAuth();
  const { storeSettings } = useStoreSettings();
  const [formData, setFormData] = useState<RegisterData>(
    initialFormData || {
      firstName: '',
      lastName: '',
      dateOfBirth: {
        year: '',
        month: '',
        day: '',
      },
      gender: 'male',
      referralCode: '',
    }
  );
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [shouldNavigateToSuccess, setShouldNavigateToSuccess] = useState(false);

  // Handle referral code from navigation state
  useEffect(() => {
    if (location.state?.referralCode) {
      setFormData(prev => ({
        ...prev,
        referralCode: location.state.referralCode,
      }));
      // Clear the navigation state to prevent re-application
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  // Handle navigation to success screen after successful registration
  useEffect(() => {
    if (shouldNavigateToSuccess) {
      navigate(routes.registerSuccess, { replace: true });
      setShouldNavigateToSuccess(false);
    }
  }, [shouldNavigateToSuccess, navigate]);

  // Validation functions
  const validateMonth = (month: string): boolean => {
    const monthNum = parseInt(month);
    return monthNum >= 1 && monthNum <= 12;
  };

  const validateDay = (day: string, month: string, year: string): boolean => {
    const dayNum = parseInt(day);
    const monthNum = parseInt(month);
    const yearNum = parseInt(year);

    if (isNaN(dayNum) || isNaN(monthNum) || isNaN(yearNum)) return false;

    const daysInMonth = new Date(yearNum, monthNum, 0).getDate();
    return dayNum >= 1 && dayNum <= daysInMonth;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.firstName || !formData.lastName || !formData.dateOfBirth.year ||
      !formData.dateOfBirth.month || !formData.dateOfBirth.day) {
      setError(t('auth.errorMessages.register.missingFields'));
      return;
    }

    // Validate month
    if (!validateMonth(formData.dateOfBirth.month)) {
      setError(t('auth.errorMessages.register.monthInvalid'));
      return;
    }

    // Validate day
    if (!validateDay(formData.dateOfBirth.day, formData.dateOfBirth.month, formData.dateOfBirth.year)) {
      setError(t('auth.errorMessages.register.dayInvalid'));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Check if we have Firebase user information
      if (!firebaseUser) {
        setError(t('auth.errorMessages.register.firebaseAuthError'));
        return;
      }

      // Use the convertRegistrationData function to create the proper structure
      const registrationData = convertRegistrationData(firebaseUser, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: firebaseUser.email || '',
        phone: firebaseUser.phoneNumber || '',
        dateOfBirth: formData.dateOfBirth,
        gender: formData.gender,
        referralCode: formData.referralCode,
      });

      // Call the register customer API
      const customer = await registerCustomer(registrationData);

      if (customer) {
        console.log('Customer registered successfully:', customer);

        // Update authentication state with both customer and Firebase user
        updateCustomer(customer, firebaseUser || undefined);

        // Set flag to navigate to success screen
        setShouldNavigateToSuccess(true);
      } else {
        setError(t('auth.errorMessages.register.registrationFailed'));
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      setError(error.message || t('auth.errorMessages.register.registrationFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => {
        const newFormData = {
          ...prev,
          [parent]: {
            ...(prev[parent as keyof RegisterData] as any),
            [child]: value,
          },
        };
        return newFormData;
      });
    } else {
      setFormData(prev => {
        const newFormData = {
          ...prev,
          [field]: value,
        };
        return newFormData;
      });
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100svh',
        height: '100svh',
        position: 'relative',
        background: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url("${storeSettings?.backgroundUrl || '/images/background.png'}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'start',
      }}
    >
      {isDesktop && (
        <Box sx={{ flex: 0.3 }}>
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              color: 'white',
              pt: 4,
              pb: 4,
            }}
          >
            <img src={storeSettings?.logoUrl || '/icons/logo.svg'} alt="Store Logo" style={{ height: '50px', width: 'auto' }} />
          </Box>
        </Box>
      )}

      <Box sx={{ flex: isDesktop ? 0.5 : 1 }}>
        <Box
          sx={{
            height: isDesktop ? 'auto' : '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
          }}
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: isDesktop ? '24px' : 0,
              background: '#FFF',
              backdropFilter: 'blur(10px)',
              p: 4,
              width: '100%',
              height: '100%',
              maxWidth: isDesktop ? '400px' : '100%',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              position: 'relative',
            }}
          >

            <Box component="form" onSubmit={handleSubmit}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <IconButton onClick={onBack} sx={{ color: 'text.secondary', ml: -2, mb: 1 }}>
                  <ChevronLeft />
                </IconButton>

                {/* Language Selector */}
                <LanguageSelector />
              </Box>

              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '20px', mb: 1, color: '#585858' }}>
                {t('auth.register.title')}
              </Typography>

              <Typography variant="body2" sx={{ mb: 5.5, color: "#585858" }}>
                {t('auth.register.subtitle')}
              </Typography>

              {/* First Name */}
              <Box sx={{ mb: 1.5 }}>
                <TextField
                  fullWidth
                  placeholder={t('auth.register.firstName')}
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      backgroundColor: '#FFF',
                      border: '1px solid #E0E0E0',
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '& .MuiInputBase-input': {
                        padding: 2,
                        height: '56px',
                        boxSizing: 'border-box',
                      },
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: '#A0A0A0',
                      opacity: 1,
                    },
                  }}
                />
              </Box>

              {/* Last Name */}
              <Box sx={{ mb: 1.5 }}>
                <TextField
                  fullWidth
                  placeholder={t('auth.register.lastName')}
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      backgroundColor: '#FFF',
                      border: '1px solid #E0E0E0',
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '& .MuiInputBase-input': {
                        padding: 2,
                        height: '56px',
                        boxSizing: 'border-box',
                      },
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: '#A0A0A0',
                      opacity: 1,
                    },
                  }}
                />
              </Box>

              {/* Referral Code */}
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  placeholder={t('auth.register.referralCode')}
                  value={formData.referralCode}
                  onChange={(e) => handleInputChange('referralCode', e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      backgroundColor: '#FFF',
                      border: '1px solid #E0E0E0',
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '& .MuiInputBase-input': {
                        padding: 2,
                        height: '56px',
                        boxSizing: 'border-box',
                      },
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: '#A0A0A0',
                      opacity: 1,
                    },
                  }}
                />
              </Box>

              {/* Date of Birth */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                  <Select
                    value={formData.dateOfBirth.month}
                    onChange={(e) => handleInputChange('dateOfBirth.month', e.target.value)}
                    displayEmpty
                    sx={{
                      borderRadius: '8px',
                      backgroundColor: '#FFF',
                      border: '1px solid #E0E0E0',
                      height: '56px',
                      flex: 1,
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '& .MuiSelect-select': {
                        padding: 2,
                        height: '56px',
                        boxSizing: 'border-box',
                        display: 'flex',
                        alignItems: 'center',
                      },
                    }}
                  >
                    <MenuItem value="" disabled sx={{ color: '#A0A0A0' }}>
                      {t('auth.register.month')}
                    </MenuItem>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                      <MenuItem key={month} value={month.toString()}>
                        {month}
                      </MenuItem>
                    ))}
                  </Select>
                  <TextField
                    placeholder={t('auth.register.day')}
                    type="number"
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                    value={formData.dateOfBirth.day}
                    onChange={(e) => handleInputChange('dateOfBirth.day', e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: '#FFF',
                        border: '1px solid #E0E0E0',
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                        '& .MuiInputBase-input': {
                          padding: 2,
                          height: '56px',
                          boxSizing: 'border-box',
                        },
                      },
                      '& .MuiInputBase-input::placeholder': {
                        color: '#A0A0A0',
                        opacity: 1,
                      },
                    }}
                  />
                  <TextField
                    placeholder={t('auth.register.year')}
                    type="number"
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                    value={formData.dateOfBirth.year}
                    onChange={(e) => handleInputChange('dateOfBirth.year', e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: '#FFF',
                        border: '1px solid #E0E0E0',
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                        '& .MuiInputBase-input': {
                          padding: 2,
                          height: '56px',
                          boxSizing: 'border-box',
                        },
                      },
                      '& .MuiInputBase-input::placeholder': {
                        color: '#A0A0A0',
                        opacity: 1,
                      },
                    }}
                  />
                </Box>
              </Box>

              {/* Gender */}
              <Box sx={{ mb: 5 }}>
                <FormControl component="fieldset">
                  <RadioGroup
                    row
                    value={formData.gender}
                    onChange={(e) => handleInputChange('gender', e.target.value)}
                  >
                    <FormControlLabel
                      value="male"
                      control={<Radio />}
                      label={t('auth.register.male')}
                      sx={{
                        ml: 0,
                        mr: 3,
                        borderRadius: '8px',
                        border: '1px solid #E0E0E0',
                        px: 2,
                        py: 0.5,
                        '& .MuiRadio-root': { order: 2 }
                      }}
                    />
                    <FormControlLabel
                      value="female"
                      control={<Radio />}
                      label={t('auth.register.female')}
                      sx={{
                        borderRadius: '8px',
                        border: '1px solid #E0E0E0',
                        px: 2,
                        py: 0.5,
                        '& .MuiRadio-root': { order: 2 }
                      }}
                    />
                  </RadioGroup>
                </FormControl>
              </Box>

              {/* Sign Up Button */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading}
                sx={{
                  width: '100%',
                  height: '44px',
                  borderRadius: '8px',
                  padding: '10px 16px',
                  textTransform: 'none',
                  backgroundColor: '#FF5413',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#d2450eff',
                  },
                  '&:disabled': {
                    backgroundColor: '#E0E0E0',
                    color: '#999',
                  },
                  fontSize: '14px',
                  fontWeight: 400,
                }}
              >
                {isLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : t('common.continue')}
              </Button>

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
} 